import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, FloppyDisk, ShieldCheck, LinkSimple } from "@phosphor-icons/react";

const TAB_LABELS = {
  guide: "Guide", inbox: "Inbox", applications: "Applications", reels: "Reels",
  live_vision: "Live Vision", case_studies: "Case studies", capabilities: "Capabilities / Products",
  systems: "Systems layers", playbooks: "Playbooks", knowledge: "Knowledge", careers: "Careers",
  files: "Files", analytics: "Analytics", deploy: "Deploy", appearance: "Site appearance", users: "User access",
};

export default function UserAccessPanel({ token }) {
  const headers = { "X-Admin-Token": token };
  const [roles, setRoles] = useState([]);
  const [tabKeys, setTabKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // new role form
  const [nrName, setNrName] = useState("");
  const [nrSlug, setNrSlug] = useState("");
  const [nrTabs, setNrTabs] = useState([]);
  // new user form
  const [nuEmail, setNuEmail] = useState("");
  const [nuName, setNuName] = useState("");
  const [nuPass, setNuPass] = useState("");
  const [nuRole, setNuRole] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: u }] = await Promise.all([
        axios.get(`${API}/admin/roles`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
      ]);
      setRoles(r.roles || []);
      setTabKeys(r.tab_keys || []);
      setUsers(u.users || []);
    } catch {
      toast.error("Could not load users/roles.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const err = (e, fb) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);

  // ---- roles ----
  const toggleNrTab = (k) => setNrTabs((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const createRole = async () => {
    if (!nrName.trim()) return toast.error("Role name required");
    try {
      await axios.post(`${API}/admin/roles`, { name: nrName, slug: nrSlug, tabs: nrTabs }, { headers });
      toast.success("Role created"); setNrName(""); setNrSlug(""); setNrTabs([]); load();
    } catch (e) { err(e, "Could not create role"); }
  };
  const toggleRoleTab = (role, k) => {
    const tabs = role.tabs.includes(k) ? role.tabs.filter((x) => x !== k) : [...role.tabs, k];
    setRoles((p) => p.map((r) => (r.id === role.id ? { ...r, tabs, _dirty: true } : r)));
  };
  const saveRole = async (role) => {
    try {
      await axios.put(`${API}/admin/roles/${role.id}`, { tabs: role.tabs }, { headers });
      toast.success(`Saved ${role.name} access`);
      setRoles((p) => p.map((r) => (r.id === role.id ? { ...r, _dirty: false } : r)));
    } catch (e) { err(e, "Could not save role"); }
  };
  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try { await axios.delete(`${API}/admin/roles/${role.id}`, { headers }); toast.success("Role deleted"); load(); }
    catch (e) { err(e, "Could not delete role"); }
  };

  // ---- users ----
  const createUser = async () => {
    if (!nuEmail || !nuPass || !nuRole) return toast.error("Email, password and role are required");
    try {
      await axios.post(`${API}/admin/users`, { email: nuEmail, name: nuName || nuEmail, password: nuPass, role_slug: nuRole }, { headers });
      toast.success("User created"); setNuEmail(""); setNuName(""); setNuPass(""); setNuRole(""); load();
    } catch (e) { err(e, "Could not create user"); }
  };
  const patchUser = async (u, patch) => {
    try { await axios.put(`${API}/admin/users/${u.id}`, patch, { headers }); toast.success("User updated"); load(); }
    catch (e) { err(e, "Could not update user"); }
  };
  const resetPass = async (u) => {
    const p = window.prompt(`New password for ${u.email} (min 6 chars):`);
    if (!p) return;
    patchUser(u, { password: p });
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await axios.delete(`${API}/admin/users/${u.id}`, { headers }); toast.success("User deleted"); load(); }
    catch (e) { err(e, "Could not delete user"); }
  };

  if (loading) return <div className="text-[#8E8E93] text-sm py-8" data-testid="user-access-loading">Loading users & roles…</div>;

  return (
    <div data-testid="user-access-panel" className="space-y-14">
      {/* ROLES */}
      <section>
        <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} color="#00E5FF" weight="duotone" /><div className="text-overline">◢ Roles & permissions</div></div>
        <p className="text-[#8E8E93] text-sm mb-5 max-w-2xl">Each role opens at <span className="font-mono text-[#DFFF00]">wisewit.ai/&lt;slug&gt;</span>. Tick the tabs a role may access (access = view + edit). The built-in <b>admin</b> role always has everything.</p>

        <div className="space-y-4">
          {roles.map((role) => (
            <div key={role.id} className="ww-card p-5" data-testid={`role-row-${role.slug}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-white font-display text-lg">{role.name}</span>
                  <a href={`/${role.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-[#8E8E93] hover:text-white flex items-center gap-1"><LinkSimple size={11} />/{role.slug}</a>
                  {role.is_system && <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#00FFB2] border border-[#00FFB2]/40 px-1.5 py-0.5">System</span>}
                </div>
                {!role.is_system && (
                  <div className="flex items-center gap-3">
                    {role._dirty && <button onClick={() => saveRole(role)} className="btn-primary text-[10px] py-2 px-3" data-testid={`role-save-${role.slug}`}><FloppyDisk size={12} /> Save</button>}
                    <button onClick={() => deleteRole(role)} className="text-[#8E8E93] hover:text-[#FF4500]" title="Delete role" data-testid={`role-delete-${role.slug}`}><Trash size={15} /></button>
                  </div>
                )}
              </div>
              {role.is_system ? (
                <div className="mt-3 font-mono text-[11px] text-[#8E8E93]">All tabs (full access)</div>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {tabKeys.map((k) => {
                    const on = role.tabs.includes(k);
                    return (
                      <button key={k} onClick={() => toggleRoleTab(role, k)} data-testid={`role-${role.slug}-tab-${k}`}
                        className={`font-mono text-[10px] tracking-[0.12em] uppercase px-2.5 py-1.5 border transition-colors ${on ? "border-[#FF4500] bg-[#FF4500]/10 text-white" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`}>
                        {TAB_LABELS[k] || k}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* new role */}
        <div className="ww-card p-5 mt-4 border-dashed" data-testid="new-role-form">
          <div className="text-overline mb-3">◢ New role</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="ww-label">Name</label><input value={nrName} onChange={(e) => setNrName(e.target.value)} className="ww-input" placeholder="Editor" data-testid="new-role-name" /></div>
            <div><label className="ww-label">URL slug (optional)</label><input value={nrSlug} onChange={(e) => setNrSlug(e.target.value)} className="ww-input" placeholder="editor → /editor" data-testid="new-role-slug" /></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {tabKeys.map((k) => (
              <button key={k} onClick={() => toggleNrTab(k)} data-testid={`new-role-tab-${k}`}
                className={`font-mono text-[10px] tracking-[0.12em] uppercase px-2.5 py-1.5 border transition-colors ${nrTabs.includes(k) ? "border-[#FF4500] bg-[#FF4500]/10 text-white" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`}>
                {TAB_LABELS[k] || k}
              </button>
            ))}
          </div>
          <button onClick={createRole} className="btn-primary mt-4" data-testid="new-role-create"><Plus size={14} /> Create role</button>
        </div>
      </section>

      {/* USERS */}
      <section>
        <div className="flex items-center gap-2 mb-5"><UsersThree size={18} color="#DFFF00" weight="duotone" /><div className="text-overline">◢ Users ({users.length})</div></div>
        <div className="border border-[#2A2A2A]">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
            <div className="col-span-3">Name</div><div className="col-span-3">Email</div><div className="col-span-2">Role</div><div className="col-span-2">Status</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {users.length === 0 && <div className="px-4 py-8 text-center text-[#555] text-sm">No users yet. Create one below (e.g. your HR teammate).</div>}
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] items-center text-sm" data-testid={`user-row-${u.email}`}>
              <div className="col-span-3 text-white truncate">{u.name}</div>
              <div className="col-span-3 font-mono text-xs text-[#DFFF00] truncate" title={u.email}>{u.email}</div>
              <div className="col-span-2">
                <select value={u.role_slug} onChange={(e) => patchUser(u, { role_slug: e.target.value })} className="ww-input py-1 text-xs" data-testid={`user-role-${u.email}`}>
                  {roles.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <button onClick={() => patchUser(u, { active: !u.active })} data-testid={`user-toggle-${u.email}`}
                  className={`font-mono text-[10px] uppercase tracking-[0.14em] px-2 py-1 border ${u.active ? "border-[#00FFB2]/50 text-[#00FFB2]" : "border-[#FF4500]/50 text-[#FF4500]"}`}>
                  {u.active ? "Active" : "Disabled"}
                </button>
              </div>
              <div className="col-span-2 flex justify-end gap-3">
                <button onClick={() => resetPass(u)} className="font-mono text-[10px] uppercase text-[#8E8E93] hover:text-white" data-testid={`user-reset-${u.email}`}>Reset pw</button>
                <button onClick={() => deleteUser(u)} className="text-[#8E8E93] hover:text-[#FF4500]" title="Delete" data-testid={`user-delete-${u.email}`}><Trash size={15} /></button>
              </div>
            </div>
          ))}
        </div>

        {/* new user */}
        <div className="ww-card p-5 mt-4 border-dashed" data-testid="new-user-form">
          <div className="text-overline mb-3">◢ New user</div>
          <div className="grid md:grid-cols-4 gap-3">
            <div><label className="ww-label">Name</label><input value={nuName} onChange={(e) => setNuName(e.target.value)} className="ww-input" placeholder="Priya" data-testid="new-user-name" /></div>
            <div><label className="ww-label">Email</label><input value={nuEmail} onChange={(e) => setNuEmail(e.target.value)} className="ww-input" placeholder="priya@wisewit.ai" data-testid="new-user-email" /></div>
            <div><label className="ww-label">Password</label><input type="password" value={nuPass} onChange={(e) => setNuPass(e.target.value)} className="ww-input" placeholder="min 6 chars" data-testid="new-user-password" /></div>
            <div><label className="ww-label">Role</label>
              <select value={nuRole} onChange={(e) => setNuRole(e.target.value)} className="ww-input" data-testid="new-user-role">
                <option value="">Select…</option>
                {roles.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
              </select>
            </div>
          </div>
          <button onClick={createUser} className="btn-primary mt-4" data-testid="new-user-create"><Plus size={14} /> Create user</button>
        </div>
      </section>
    </div>
  );
}
